import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/api/client";
import { unwrap, type ApiError } from "@/api/errors";
import { queryKeys } from "@/api/queryKeys";
import type { MemberInvite, MemberOut, OrgOut } from "@/api/types";

export type { MemberOut, OrgOut } from "@/api/types";

export function useOrgs() {
  return useQuery<OrgOut[], ApiError>({
    queryKey: queryKeys.orgs,
    queryFn: async () => unwrap(await api.GET("/orgs")),
  });
}

export function useMembers(orgSlug: string) {
  return useQuery<MemberOut[], ApiError>({
    queryKey: queryKeys.members(orgSlug),
    queryFn: async () => unwrap(await api.GET("/orgs/{slug}/members", { params: { path: { slug: orgSlug } } })),
  });
}

export function useInviteMember(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<MemberOut, ApiError, MemberInvite>({
    mutationFn: async (body) =>
      unwrap(await api.POST("/orgs/{slug}/members/invite", { params: { path: { slug: orgSlug } }, body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.members(orgSlug) }),
  });
}

export function useRemoveMember(orgSlug: string) {
  const qc = useQueryClient();
  return useMutation<void, ApiError, string>({
    mutationFn: async (userId) => {
      unwrap(
        await api.DELETE("/orgs/{slug}/members/{user_id}", { params: { path: { slug: orgSlug, user_id: userId } } }),
      );
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: queryKeys.members(orgSlug) }),
  });
}
